import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import {
    Users,
    Check,
    X,
    Clock,
    Search,
    Calendar,
    MapPin,
    Eye,
    Edit,
    Trash2,
    Save,
    Activity as ActivityIcon,
    ArrowLeft,
    Plus,
    FileText,
    UserPlus,
} from 'lucide-react';
import {
    ApplicationStatus,
    statusConfig,
    Application,
    ApplicationWithApplicant,
    ApplicationWithApplicantActivity
} from '../types/application';
import { apiService } from '../services/api.service';
import { Activity, ActivityFormData } from '../types/activity';
import { useAuth } from '../contexts/AuthContext';
import { updateLanguageServiceSourceFile } from 'typescript';



export function ActivityManagement() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    // const [applications, setApplications] = useState<any[]>(mockApplications);
    const [applications, setApplications] = useState<ApplicationWithApplicantActivity[]>([]);
    // const [activities, setActivities] = useState<Activity[]>([]);
    const [createdActivities, setCreatedActivities] = useState<Activity[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [editingActivity, setEditingActivity] = useState<any>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const getNumberOfParticipations = async (activityId: string) => {
        const participations = await apiService.getParticipationsByActivityId(activityId);
        return participations.length;
    }

    const fetchActivities = async () => {
        const activitiesCreated = await apiService.getActivitiesByCreatorId(user!.id);
        const activitiesWithParticipations = await Promise.all(
            activitiesCreated.map(async (activity) => ({
                ...activity,
                numberOfParticipations: await getNumberOfParticipations(activity.id)
            }))
        );
        setCreatedActivities(activitiesWithParticipations);
        return activitiesWithParticipations as Activity[];
    }

    const fetchApplicant = async (application: Application) => {
        // each applications has one applicant
        const applicant = await apiService.getUserById(application.userId);

        return {
            id: application.id,
            activityId: application.activityId,
            status: application.status,
            note: application.note,
            appliedDate: new Date(application.createdAt!),
            applicantName: applicant.name,
            applicantEmail: applicant.email,
            applicantAvatar: applicant.avatar
        } as ApplicationWithApplicant;
    }

    const fetchApplicationsWithApplicantActivity = async (activity: any) => {
        // for one activity, we have a list of ApplicationWithApplicant
        const applications = await apiService.getApplicationsByActivityId(activity.id);
        const applicationsWithApplicant = await Promise.all(applications.map(fetchApplicant));

        // and then we inject activityId and activityTitle to the object
        const applicationsWithApplicantActivity = applicationsWithApplicant.map(
            obj => ({ ...obj, activityTitle: activity.title })
        );
        return applicationsWithApplicantActivity as ApplicationWithApplicantActivity[];
    }

    const fetchActivitiesWithApplicationApplicant = async () => {
        // all the activites created by the user
        const activitiesCreated = await fetchActivities();

        // list of list of ApplicationWithApplicantActivity for each activity
        const activitiesWithApplicationApplicant = await Promise.all(
            activitiesCreated.map(fetchApplicationsWithApplicantActivity)
        );

        // after flat, we have a list of ApplicationWithApplicantActivity
        // so we can set the applications state
        setApplications(activitiesWithApplicationApplicant.flat());
    }

    useEffect(() => {
        fetchActivitiesWithApplicationApplicant();
    }, [])

    // Get the current view from URL params
    const currentView = searchParams.get('view') || 'activities';
    const selectedActivityId = searchParams.get('activity');

    // Set view in URL
    const setView = (view: string, activityId?: string) => {
        const params = new URLSearchParams();
        params.set('view', view);
        if (activityId) {
            params.set('activity', activityId);
        }
        setSearchParams(params);
    };

    // Filter applications based on current view and selected activity
    const filteredApplications = applications.filter(app => {
        const matchesSearch = app.applicantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.applicantEmail.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = selectedStatus === 'all' || app.status === selectedStatus;
        const matchesActivity = selectedActivityId ? app.activityId === selectedActivityId : true;

        return matchesSearch && matchesStatus && matchesActivity;
    });

    const handleStatusChange = async (applicationId: string, newStatus: ApplicationStatus) => {
        try {
            await apiService.updateApplication(applicationId, newStatus);
        } catch (error) {
            toast.error('Failed to update application status');
        }
        setApplications(prev =>
            prev.map(app =>
                app.id === applicationId ? { ...app, status: newStatus } : app
            )
        );
    };

    const handleEditActivity = (activity: any) => {
        setEditingActivity({ ...activity });
        setIsEditModalOpen(true);
    };

    const handleSaveActivity = async (editingActivityId: string, editingActivity: ActivityFormData) => {
        // explicitly construct the updatedActivityData object
        // to avoid sending the whole activity object, which contains the id, createdAt, updatedAt, etc.
        // which are columns that are not allowed by the backend DTO to be updated
        const updatedActivityData = {
            title: editingActivity.title,
            type: editingActivity.type,
            dateTime: new Date(editingActivity.dateTime),
            // TODO: update location here with autocomplete location box
            // just like the create activity page
            // location: editingActivity.location,
            description: editingActivity.description,
            maxParticipants: editingActivity.maxParticipants,
            difficulty: editingActivity.difficulty,
            price: editingActivity.price,
            image: editingActivity.image,
            // isFree: editingActivity.isFree,
            // durationMinutes: editingActivity.durationMinutes,
        }

        try {
            await apiService.updateActivity(editingActivityId, updatedActivityData as any);
            toast.success('Activity updated successfully');
        } catch (error) {
            toast.error('Failed to update activity');
        }
        setCreatedActivities(prev =>
            prev.map(activity =>
                activity.id === editingActivityId ? { ...activity, ...editingActivity } : activity
            )
        );
        setIsEditModalOpen(false);
        setEditingActivity(null);
    };

    const handleDeleteActivity = async (activityId: string) => {
        try {
            await apiService.deleteActivity(activityId);
            toast.success('Activity deleted successfully');
            setCreatedActivities(prev => prev.filter(activity => activity.id !== activityId));
        } catch (error) {
            toast.error('Failed to delete activity');
        }

    };

    const handleManageApplications = (activityId: string) => {
        setView('applications', activityId);
    };

    const getSelectedActivity = () => {
        return createdActivities.find(activity => activity.id === selectedActivityId);
    };

    const getApplicationCount = (activityId: string, status?: ApplicationStatus) => {
        const activityApplications = applications.filter((app: any) => app.activityId === activityId);
        if (status) {
            return activityApplications.filter((app: any) => app.status === status).length;
        }
        return activityApplications.length;
    };

    const getAllApplicationsByStatus = (status?: ApplicationStatus) => {
        if (status) {
            return applications.filter(app => app.status === status);
        }
        return applications;
    };

    const renderApplicationCard = (application: any) => {
        const statusInfo = statusConfig[application.status as keyof typeof statusConfig];
        const StatusIcon = statusInfo.icon;

        return (
            <Card key={application.id} className="mb-4">
                <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex items-start space-x-3 flex-1 min-w-0">
                            <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                                <AvatarImage src={application.applicantAvatar} alt={application.applicantName} />
                                <AvatarFallback>{application.applicantName.slice(0, 2).toUpperCase()}</AvatarFallback>
                            </Avatar>

                            <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                                    <h3 className="font-medium text-sm sm:text-base truncate">{application.applicantName}</h3>
                                    <Badge className={`${statusInfo.color} text-xs flex-shrink-0`}>
                                        <StatusIcon className="w-3 h-3 mr-1" />
                                        {statusInfo.label}
                                    </Badge>
                                </div>

                                <p className="text-xs sm:text-sm text-muted-foreground mb-1 truncate">{application.applicantEmail}</p>
                                <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                                    Applied {new Date(application.appliedDate).toLocaleDateString()} • {"todo: application.experience"} level
                                </p>

                                {application.note && (
                                    <div className="bg-muted p-3 rounded-lg mb-3">
                                        <p className="text-xs sm:text-sm break-words">Application note: {application.note}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {(application.status === ApplicationStatus.PENDING) && (
                            <div className="flex flex-row sm:flex-col gap-2 sm:ml-4 flex-shrink-0">
                                <Button
                                    size="sm"
                                    onClick={() => handleStatusChange(application.id, ApplicationStatus.APPROVED)}
                                    className="bg-green-600 hover:bg-green-700 text-xs px-2 py-1 h-auto"
                                >
                                    <Check className="w-3 h-3 mr-1" />
                                    <span className="hidden sm:inline">Approve</span>
                                    <span className="sm:hidden">✓</span>
                                </Button>

                                {application.status === ApplicationStatus.PENDING && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleStatusChange(application.id, ApplicationStatus.PENDING)}
                                        className="text-xs px-2 py-1 h-auto"
                                    >
                                        <Users className="w-3 h-3 mr-1" />
                                        <span className="hidden sm:inline">Wait List</span>
                                        <span className="sm:hidden">Wait</span>
                                    </Button>
                                )}

                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleStatusChange(application.id, ApplicationStatus.REJECTED)}
                                    className="text-xs px-2 py-1 h-auto"
                                >
                                    <X className="w-3 h-3 mr-1" />
                                    <span className="hidden sm:inline">Decline</span>
                                    <span className="sm:hidden">✗</span>
                                </Button>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        );
    };

    const renderActivityCard = (activity: any) => {
        const getStatusColor = (status: string) => {
            switch (status) {
                case 'upcoming': return 'bg-blue-100 text-blue-800';
                case 'full': return 'bg-green-100 text-green-800';
                case 'cancelled': return 'bg-red-100 text-red-800';
                default: return 'bg-gray-100 text-gray-800';
            }
        };

        const totalApplications = getApplicationCount(activity.id);
        const pendingApplications = getApplicationCount(activity.id, ApplicationStatus.PENDING);

        return (
            <Card key={activity.id} className="mb-4">
                <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full sm:w-32 h-32 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                            <img
                                src={activity.image}
                                alt={activity.title}
                                className="w-full h-full object-cover"
                            />
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-medium text-lg truncate">{activity.title}</h3>
                                    <div className="flex flex-wrap items-center gap-2 mt-1">
                                        <Badge variant="outline" className="text-xs">
                                            {activity.type}
                                        </Badge>
                                        <Badge className={`${getStatusColor(activity.status)} text-xs`}>
                                            {activity.status}
                                        </Badge>
                                        <Badge variant="outline" className="text-xs">
                                            {activity.difficulty}
                                        </Badge>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
                                        <DialogTrigger asChild>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleEditActivity(activity)}
                                                className="text-xs"
                                            >
                                                <Edit className="w-3 h-3 mr-1" />
                                                Edit
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                            <DialogHeader>
                                                <DialogTitle>Edit Activity</DialogTitle>
                                                <DialogDescription>
                                                    Update the details of your activity. Changes will be saved immediately.
                                                </DialogDescription>
                                            </DialogHeader>

                                            {editingActivity && (
                                                <div className="space-y-4 mt-4">
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <Label htmlFor="edit-title">Title</Label>
                                                            <Input
                                                                id="edit-title"
                                                                value={editingActivity.title}
                                                                onChange={(e) => setEditingActivity((prev: any) => ({ ...prev, title: e.target.value }))}
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label htmlFor="edit-type">Type</Label>
                                                            <Input
                                                                id="edit-type"
                                                                value={editingActivity.type}
                                                                onChange={(e) => setEditingActivity((prev: any) => ({ ...prev, type: e.target.value }))}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <Label htmlFor="edit-date">Date</Label>
                                                            <Input
                                                                id="edit-date"
                                                                type="date"
                                                                value={editingActivity.date}
                                                                onChange={(e) => setEditingActivity((prev: any) => ({ ...prev, date: e.target.value }))}
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label htmlFor="edit-time">Time</Label>
                                                            <Input
                                                                id="edit-time"
                                                                type="time"
                                                                value={editingActivity.time}
                                                                onChange={(e) => setEditingActivity((prev: any) => ({ ...prev, time: e.target.value }))}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <Label htmlFor="edit-location">Location</Label>
                                                        <Input
                                                            id="edit-location"
                                                            value={editingActivity.location?.name.split(',')[0]}
                                                            onChange={(e) => setEditingActivity((prev: any) => ({ ...prev, location: e.target.value }))}
                                                        />
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <Label htmlFor="edit-participants">Max Participants</Label>
                                                            <Input
                                                                id="edit-participants"
                                                                type="number"
                                                                value={editingActivity.maxParticipants}
                                                                onChange={(e) => setEditingActivity((prev: any) => ({ ...prev, maxParticipants: parseInt(e.target.value) }))}
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label htmlFor="edit-price">Price ($)</Label>
                                                            <Input
                                                                id="edit-price"
                                                                type="number"
                                                                value={editingActivity.price}
                                                                onChange={(e) => setEditingActivity((prev: any) => ({ ...prev, price: parseFloat(e.target.value) }))}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <Label htmlFor="edit-description">Description</Label>
                                                        <Textarea
                                                            id="edit-description"
                                                            value={editingActivity.description}
                                                            onChange={(e) => setEditingActivity((prev: any) => ({ ...prev, description: e.target.value }))}
                                                            rows={3}
                                                        />
                                                    </div>

                                                    <div className="flex justify-end gap-2 pt-4">
                                                        <Button
                                                            variant="outline"
                                                            onClick={() => setIsEditModalOpen(false)}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button onClick={() => handleSaveActivity(editingActivity.id, editingActivity as ActivityFormData)}>
                                                            <Save className="w-4 h-4 mr-2" />
                                                            Save Changes
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </DialogContent>
                                    </Dialog>

                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                className="text-xs"
                                            >
                                                <Trash2 className="w-3 h-3 mr-1" />
                                                Delete
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Delete Activity</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    Are you sure you want to delete "{activity.title}"? This action cannot be undone.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction
                                                    onClick={() => handleDeleteActivity(activity.id)}
                                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                >
                                                    Delete
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4">
                                <div className="flex items-center space-x-2">
                                    <Calendar className="w-4 h-4 text-muted-foreground" />
                                    <span>
                                        {new Date(activity.dateTime).toLocaleDateString()}
                                        {" at "}
                                        {new Date(activity.dateTime).toLocaleTimeString(
                                            [],
                                            { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <MapPin className="w-4 h-4 text-muted-foreground" />
                                    <span className="truncate">{activity.location?.name.split(',')[0]}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Users className="w-4 h-4 text-muted-foreground" />
                                    <span>{activity.numberOfParticipations}/{activity.maxParticipants} participants</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className="font-medium">Price: ${activity.price}</span>
                                </div>
                            </div>

                            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                                {activity.description}
                            </p>

                            <div className="flex flex-wrap gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => navigate(`/activity/${activity.id}`)}
                                    className="text-xs"
                                >
                                    <Eye className="w-3 h-3 mr-1" />
                                    View Details
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleManageApplications(activity.id)}
                                    className="text-xs"
                                >
                                    <Users className="w-3 h-3 mr-1" />
                                    Manage Applications
                                    {totalApplications > 0 && (
                                        <Badge variant="secondary" className="ml-2 text-xs">
                                            {totalApplications}
                                            {pendingApplications > 0 && (
                                                <span className="text-yellow-600"> · {pendingApplications} pending</span>
                                            )}
                                        </Badge>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    const selectedActivity = getSelectedActivity();

    // Calculate counts for tabs
    const activitiesCount = createdActivities.length;
    const allApplicationsCount = applications.length;
    const pendingApplicationsCount = getAllApplicationsByStatus(ApplicationStatus.PENDING).length;
    // const approvedApplicationsCount = getAllApplicationsByStatus(ApplicationStatus.APPROVED).length;
    // const waitingApplicationsCount = getAllApplicationsByStatus(ApplicationStatus.PENDING).length;
    // const declinedApplicationsCount = getAllApplicationsByStatus(ApplicationStatus.REJECTED).length;


    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
                <div className="mb-6 sm:mb-8">
                    <h1 className="text-2xl sm:text-3xl font-bold mb-2">Activity Management</h1>
                    <p className="text-sm sm:text-base text-muted-foreground">
                        Manage your activities and review applications from participants
                    </p>
                </div>

                {currentView === 'applications' && selectedActivityId ? (
                    <div>
                        <div className="flex items-center gap-4 mb-6">
                            <Button
                                variant="ghost"
                                onClick={() => setView('activities')}
                                className="flex items-center gap-2"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back to Activities
                            </Button>
                            <div className="flex-1">
                                <h2 className="text-xl font-semibold">
                                    Applications for {selectedActivity?.title || 'Activity'}
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    Review and manage applications from participants
                                </p>
                            </div>
                        </div>

                        <div className="mb-6 flex flex-col gap-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
                                <Input
                                    placeholder="Search applications..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9 text-sm"
                                />
                            </div>

                            <div className="flex gap-2">
                                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                                    <SelectTrigger className="text-sm w-48">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Statuses</SelectItem>
                                        <SelectItem value={ApplicationStatus.PENDING}>Pending</SelectItem>
                                        <SelectItem value={ApplicationStatus.APPROVED}>Approved</SelectItem>
                                        <SelectItem value={ApplicationStatus.PENDING}>Waiting List</SelectItem>
                                        <SelectItem value={ApplicationStatus.REJECTED}>Declined</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {filteredApplications.length === 0 ? (
                                <Card>
                                    <CardContent className="p-8 text-center">
                                        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                        <h3 className="text-lg font-medium mb-2">No applications found</h3>
                                        <p className="text-muted-foreground">
                                            {searchTerm || selectedStatus !== 'all'
                                                ? 'No applications match your current filters.'
                                                : 'No one has applied to this activity yet.'
                                            }
                                        </p>
                                    </CardContent>
                                </Card>
                            ) : (
                                filteredApplications.map(renderApplicationCard)
                            )}
                        </div>
                    </div>
                ) : (
                    <Tabs
                        value={currentView === 'all-applications' ? 'all-applications' : 'activities'}
                        onValueChange={(value) => setView(value)}
                        className="space-y-6"
                    >
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="activities" className="flex items-center gap-2">
                                <ActivityIcon className="w-4 h-4" />
                                My Activities ({activitiesCount})
                            </TabsTrigger>
                            <TabsTrigger value="all-applications" className="flex items-center gap-2">
                                <UserPlus className="w-4 h-4" />
                                All Applications ({allApplicationsCount})
                                {pendingApplicationsCount > 0 && (
                                    <Badge variant="secondary" className="ml-1 text-xs">
                                        {pendingApplicationsCount} pending
                                    </Badge>
                                )}
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="activities">
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-semibold">My Activities</h2>
                                    <Button
                                        onClick={() => navigate('/create-activity')}
                                        className="flex items-center gap-2"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Create New Activity
                                    </Button>
                                </div>

                                <div className="space-y-4">
                                    {createdActivities.length === 0 ? (
                                        <Card>
                                            <CardContent className="p-8 text-center">
                                                <ActivityIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                                <h3 className="text-lg font-medium mb-2">No activities yet</h3>
                                                <p className="text-muted-foreground mb-4">
                                                    You haven't created any activities yet. Start by creating your first activity!
                                                </p>
                                                <Button onClick={() => navigate('/create-activity')}>
                                                    <Plus className="w-4 h-4 mr-2" />
                                                    Create Activity
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    ) : (
                                        createdActivities.map(renderActivityCard)
                                    )}
                                </div>
                            </div>
                        </TabsContent>

                        <TabsContent value="all-applications">
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-xl font-semibold">All Applications</h2>
                                        <p className="text-sm text-muted-foreground">
                                            Review applications across all your activities
                                        </p>
                                    </div>
                                </div>

                                <div className="mb-6 flex flex-col gap-3">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
                                        <Input
                                            placeholder="Search all applications..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="pl-9 text-sm"
                                        />
                                    </div>

                                    <div className="flex gap-2">
                                        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                                            <SelectTrigger className="text-sm w-48">
                                                <SelectValue placeholder="Status" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Statuses</SelectItem>
                                                <SelectItem value={ApplicationStatus.PENDING}>Pending</SelectItem>
                                                <SelectItem value={ApplicationStatus.APPROVED}>Approved</SelectItem>
                                                <SelectItem value={ApplicationStatus.PENDING}>Waiting List</SelectItem>
                                                <SelectItem value={ApplicationStatus.REJECTED}>Declined</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {filteredApplications.length === 0 ? (
                                        <Card>
                                            <CardContent className="p-8 text-center">
                                                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                                <h3 className="text-lg font-medium mb-2">No applications found</h3>
                                                <p className="text-muted-foreground">
                                                    {searchTerm || selectedStatus !== 'all'
                                                        ? 'No applications match your current filters.'
                                                        : 'No applications have been submitted to your activities yet.'
                                                    }
                                                </p>
                                            </CardContent>
                                        </Card>
                                    ) : (
                                        filteredApplications.map((application) => (
                                            <Card key={application.id} className="mb-4">
                                                <CardContent className="p-4 sm:p-6">
                                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                                        <div className="flex items-start space-x-3 flex-1 min-w-0">
                                                            <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                                                                <AvatarImage src={application.applicantAvatar} alt={application.applicantName} />
                                                                <AvatarFallback>{application.applicantName.slice(0, 2).toUpperCase()}</AvatarFallback>
                                                            </Avatar>

                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                                                                    <h3 className="font-medium text-sm sm:text-base truncate">{application.applicantName}</h3>
                                                                    <Badge className={`${statusConfig[application.status].color} text-xs flex-shrink-0`}>
                                                                        {React.createElement(statusConfig[application.status].icon, { className: "w-3 h-3 mr-1" })}
                                                                        {statusConfig[application.status].label}
                                                                    </Badge>
                                                                </div>

                                                                <p className="text-xs sm:text-sm text-muted-foreground mb-1 truncate">{application.applicantEmail}</p>

                                                                <div className="bg-muted p-3 rounded-lg mb-3">
                                                                    <div className="flex items-center space-x-2 mb-2">
                                                                        <Calendar className="w-4 h-4 text-muted-foreground" />
                                                                        <span className="font-medium text-sm">{application.activityTitle}</span>
                                                                    </div>
                                                                    <p className="text-xs text-muted-foreground">
                                                                        Applied {new Date(application.appliedDate).toLocaleDateString()} • {"todo: application.experience"} level
                                                                    </p>
                                                                </div>

                                                                {application.note && (
                                                                    <div className="bg-secondary p-3 rounded-lg mb-3">
                                                                        <p className="text-xs sm:text-sm break-words">Application note: {application.note}</p>
                                                                    </div>
                                                                )}

                                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => setView('applications', application.activityId)}
                                                                        className="text-primary hover:text-primary text-xs sm:text-sm h-auto p-1 self-start sm:self-center"
                                                                    >
                                                                        <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                                                        View Activity Applications
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {(application.status === ApplicationStatus.PENDING) && (
                                                            <div className="flex flex-row sm:flex-col gap-2 sm:ml-4 flex-shrink-0">
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => handleStatusChange(application.id, ApplicationStatus.APPROVED)}
                                                                    className="bg-green-600 hover:bg-green-700 text-xs px-2 py-1 h-auto"
                                                                >
                                                                    <Check className="w-3 h-3 mr-1" />
                                                                    <span className="hidden sm:inline">Approve</span>
                                                                    <span className="sm:hidden">✓</span>
                                                                </Button>

                                                                {application.status === ApplicationStatus.PENDING && (
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() => handleStatusChange(application.id, ApplicationStatus.PENDING)}
                                                                        className="text-xs px-2 py-1 h-auto"
                                                                    >
                                                                        <Users className="w-3 h-3 mr-1" />
                                                                        <span className="hidden sm:inline">Wait List</span>
                                                                        <span className="sm:hidden">Wait</span>
                                                                    </Button>
                                                                )}

                                                                <Button
                                                                    variant="destructive"
                                                                    size="sm"
                                                                    onClick={() => handleStatusChange(application.id, ApplicationStatus.REJECTED)}
                                                                    className="text-xs px-2 py-1 h-auto"
                                                                >
                                                                    <X className="w-3 h-3 mr-1" />
                                                                    <span className="hidden sm:inline">Decline</span>
                                                                    <span className="sm:hidden">✗</span>
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))
                                    )}
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                )}
            </div>
        </div>
    );
}